import { queryOptions, useQuery } from '@tanstack/react-query'
import { resolvePollingInterval } from '@omnea-app/fe-api-client/resolvePollingInterval'
import { supplierPortalPath } from '@omnea-app/fe-api-client/supplierPortalPath'
import { useAuthenticatedUser, useIsSupplierRole } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { GetRequestFormsResponse } from '@omnea-app/requests/requestsFrontendClient'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'

export const requestFormsQueryKey = 'requestForms'

interface UseGetRequestFormsOptions<TData = GetRequestFormsResponse> {
  refetchInterval?: number
  skip?: boolean
  select?: (data: GetRequestFormsResponse) => TData
}

type GetRequestFormsQueryParams = {
  requestId: string
  getToken: () => Promise<string>
  isSupplier?: boolean
}

export const getRequestFormsQueryOptions = ({ requestId, getToken, isSupplier = false }: GetRequestFormsQueryParams) =>
  queryOptions({
    queryKey: [requestFormsQueryKey, requestId],
    queryFn: async () => {
      const authorization = await getToken()
      const requestsClient = new RequestsHttpClient({ authorization })
      const { data, error } = await requestsClient.GET(supplierPortalPath('/request-forms/{requestId}', isSupplier), {
        params: { path: { requestId } },
      })
      throwIfApiError(error)
      // only for enums
      return data as GetRequestFormsResponse
    },
  })

export const useGetRequestForms = <TData = GetRequestFormsResponse>(
  { requestId }: { requestId: string },
  options?: UseGetRequestFormsOptions<TData>
) => {
  const { getToken } = useAuthenticatedUser()
  const isSupplier = useIsSupplierRole()
  return useQuery({
    ...getRequestFormsQueryOptions({ requestId, getToken, isSupplier }),
    enabled: !options?.skip,
    refetchInterval: (query) => resolvePollingInterval(query.state.error, options?.refetchInterval),
    select: options?.select,
  })
}
